import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { getBlogPost, isBlogHubPath } from "./blogContent";
import { AboutPage } from "./components/AboutPage";
import { BlogHubPage } from "./components/BlogHubPage";
import { BlogPostPage } from "./components/BlogPostPage";
import { Download } from "./components/Download";
import { FAQ } from "./components/FAQ";
import { Features } from "./components/Features";
import { Footer } from "./components/Footer";
import { Hero } from "./components/Hero";
import { LegalPage } from "./components/LegalPage";
import { Navbar } from "./components/Navbar";
import { Pricing } from "./components/Pricing";
import { SeoContent } from "./components/SeoContent";
import { Showcase } from "./components/Showcase";
import { getPageContent } from "./pageContent";

const SOCIAL_PROOF_ITEMS = [
	{ label: "Use cases", value: "Demos, tutorials, bug reports" },
	{ label: "Workflow", value: "Record, edit, and export in one desktop app" },
	{ label: "Polish", value: "Zoom, cursor effects, frames, and annotations" },
	{ label: "Shipping", value: "Cross-platform downloads available now" },
];

function SocialProofStrip() {
	return (
		<section aria-label="Product highlights" className="pb-10 md:pb-16">
			<div className="mx-auto max-w-6xl px-6">
				<div className="grid gap-4 rounded-[1.75rem] border border-white/[0.06] bg-white/[0.03] p-5 backdrop-blur-xl md:grid-cols-4 md:p-6">
					{SOCIAL_PROOF_ITEMS.map((item) => (
						<div
							key={item.label}
							className="rounded-2xl border border-white/[0.05] bg-black/10 p-4"
						>
							<div className="text-[11px] uppercase tracking-[0.18em] text-white/35">
								{item.label}
							</div>
							<p className="mt-2 text-sm leading-relaxed text-white/75">{item.value}</p>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}

function Telemetry() {
	return (
		<>
			<Analytics />
			<SpeedInsights />
		</>
	);
}

interface AppProps {
	pathname?: string;
	includeAnalytics?: boolean;
}

export default function App({ pathname: pathnameOverride, includeAnalytics = true }: AppProps) {
	const pathname =
		pathnameOverride ?? (typeof window !== "undefined" ? window.location.pathname : "/");
	const isBlogHub = isBlogHubPath(pathname);
	const blogPost = getBlogPost(pathname);
	const legalPage =
		pathname === "/privacy/" || pathname === "/privacy" || pathname === "/privacy/index.html"
			? "privacy"
			: pathname === "/terms/" || pathname === "/terms" || pathname === "/terms/index.html"
				? "terms"
				: null;

	if (legalPage) {
		return (
			<div className="min-h-screen bg-grid">
				<LegalPage kind={legalPage} />
				{includeAnalytics ? <Telemetry /> : null}
			</div>
		);
	}

	if (isBlogHub) {
		return (
			<div className="min-h-screen bg-grid">
				<Navbar pathname={pathname} />
				<main>
					<BlogHubPage />
				</main>
				<Footer pathname={pathname} />
				{includeAnalytics ? <Telemetry /> : null}
			</div>
		);
	}

	if (pathname === "/about/" || pathname === "/about" || pathname === "/about/index.html") {
		return (
			<div className="min-h-screen bg-grid">
				<Navbar pathname={pathname} />
				<main>
					<AboutPage />
				</main>
				<Footer pathname={pathname} />
				{includeAnalytics ? <Telemetry /> : null}
			</div>
		);
	}

	if (blogPost) {
		return (
			<div className="min-h-screen bg-grid">
				<Navbar pathname={pathname} />
				<main>
					<BlogPostPage post={blogPost} />
				</main>
				<Footer pathname={pathname} />
				{includeAnalytics ? <Telemetry /> : null}
			</div>
		);
	}

	const page = getPageContent(pathname);
	const isHomePage = pathname === "/" || pathname === "/index.html";

	return (
		<div className="min-h-screen bg-grid">
			<Navbar pathname={pathname} />
			<main>
				<Hero
					badge={page.badge}
					heroLineOne={page.heroLineOne}
					heroLineTwo={page.heroLineTwo}
					heroDescription={page.heroDescription}
				/>
				<SocialProofStrip />
				<Features
					headingPrefix={page.featureHeadingPrefix}
					headingAccent={page.featureHeadingAccent}
					description={page.featureDescription}
				/>
				<Showcase />
				<SeoContent
					headingPrefix={page.seoHeadingPrefix}
					headingAccent={page.seoHeadingAccent}
					description={page.seoDescription}
					useCases={page.useCases}
					comparison={page.comparison}
					sources={page.sources}
					switchGuide={page.switchGuide}
					faqs={isHomePage ? [] : page.faqs}
					relatedLinks={page.relatedLinks}
				/>
				<Pricing />
				<Download description={page.downloadDescription} />
				{isHomePage ? <FAQ items={page.faqs} /> : null}
			</main>
			<Footer pathname={pathname} />
			{includeAnalytics ? <Telemetry /> : null}
		</div>
	);
}
